import { useState } from 'react';
import { Save, FileText, Image, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useSettings } from '../../contexts/SettingsContext';

export default function AdminAbout() {
  const { settings, updateSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [aboutData, setAboutData] = useState({
    // Hero section fields
    heroSubtitle: settings.heroSubtitle || 'Discover the best properties for sale and rent in the area',
    heroTitle: settings.heroTitle || 'Invest today in<br><span class="text-[#FFE5A0]">your dream home</span>',
    heroWelcomeText: settings.heroWelcomeText || 'Welcome to Wageh Real Estate',
    // About page fields
    title: settings.aboutTitle || 'Wageh Real Estate',
    subtitle: settings.aboutSubtitle || 'More than 10 years of experience in the real estate market, we help you find the right place for you with the best choice and least effort.',
    description: settings.aboutDescription || 'We provide buying, selling, and renting real estate services (apartments - lands - buildings). We understand that choosing a property is not easy, so we focus on providing you with suitable options and we will be with you step by step until you reach the best choice for you.',
    mission: settings.aboutMission || 'We help you find the place that suits your needs and makes your investment successful.',
    vision: settings.aboutVision || 'To be the leader in the Egyptian real estate market by providing reliable services and meeting the needs of the changing market.',
    values: settings.aboutValues || 'Trust - Excellence - Reliability - Professional Ethics - Commitment - Integration',
    teamDescription: settings.aboutTeamDescription || 'A team of experts in the real estate market with over 10 years of experience'
  });

  const handleInputChange = (field: string, value: string) => {
    setAboutData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({
        // Hero section fields
        heroSubtitle: aboutData.heroSubtitle,
        heroTitle: aboutData.heroTitle,
        heroWelcomeText: aboutData.heroWelcomeText,
        // About page fields
        aboutTitle: aboutData.title,
        aboutSubtitle: aboutData.subtitle,
        aboutDescription: aboutData.description,
        aboutMission: aboutData.mission,
        aboutVision: aboutData.vision,
        aboutValues: aboutData.values,
        aboutTeamDescription: aboutData.teamDescription
      });
      toast.success('About page updated successfully!');
    } catch (error) {
      console.error('Error updating about page:', error);
      toast.error('Error updating page. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open('/about', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-800">تعديل صفحة "من نحن"</h1>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handlePreview}
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                معاينة
              </Button>
              <Button 
                onClick={handleSave} 
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-white"
                disabled={saving}
              >
                <Save className="w-4 h-4 ml-2" />
                {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="max-w-4xl mx-auto space-y-6">
          {/* Hero Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-[#D4AF37]" />
              Hero Section
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Hero Subtitle</label>
                <Textarea
                  placeholder="Discover the best properties for sale and rent in the area"
                  value={aboutData.heroSubtitle}
                  onChange={(e) => handleInputChange('heroSubtitle', e.target.value)}
                  rows={2}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Hero Title (HTML allowed)</label>
                <Textarea
                  placeholder="Invest today in<br><span class=&quot;text-[#FFE5A0]&quot;>your dream home</span>"
                  value={aboutData.heroTitle}
                  onChange={(e) => handleInputChange('heroTitle', e.target.value)}
                  rows={3}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">You can use HTML tags like &lt;br&gt; and &lt;span&gt; for styling</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Welcome Text</label>
                <Input
                  placeholder="Welcome to Wageh Real Estate"
                  value={aboutData.heroWelcomeText}
                  onChange={(e) => handleInputChange('heroWelcomeText', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              About Page Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الرئيسي</label>
                <Input
                  placeholder="عنوان الصفحة"
                  value={aboutData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الفرعي</label>
                <Input
                  placeholder="عنوان فرعي للصفحة"
                  value={aboutData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">الوصف الرئيسي</label>
              <Textarea
                placeholder="وصف شامل للشركة أو الصفحة"
                value={aboutData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                required
              />
            </div>
          </div>

          {/* Company Values */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">قيم الشركة ورؤيتها</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">المهمة</label>
                <Textarea
                  placeholder="مهمة الشركة والهدف الرئيسي"
                  value={aboutData.mission}
                  onChange={(e) => handleInputChange('mission', e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">الرؤية</label>
                <Textarea
                  placeholder="رؤية الشركة وأهدافها المستقبلية"
                  value={aboutData.vision}
                  onChange={(e) => handleInputChange('vision', e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">القيم</label>
                <Input
                  placeholder="قيم الشركة (مفصولة بـ - )"
                  value={aboutData.values}
                  onChange={(e) => handleInputChange('values', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Team Description */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">وصف الفريق</h2>
            <div>
              <label className="block text-sm font-medium mb-2">وصف الفريق</label>
              <Textarea
                placeholder="وصف مختصر للفريق وخبراته"
                value={aboutData.teamDescription}
                onChange={(e) => handleInputChange('teamDescription', e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4 text-blue-800">معاينة مباشرة</h2>
            <div className="bg-white rounded-lg p-6 border border-blue-200">
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-[#8B7109] mb-2">{aboutData.title}</h1>
                <p className="text-lg text-gray-600">{aboutData.subtitle}</p>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-[#8B7109] mb-3">عن دار العقار</h3>
                  <p className="text-gray-700 leading-relaxed">{aboutData.description}</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">مهمتنا</h4>
                    <p className="text-gray-700">{aboutData.mission}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">رؤيتنا</h4>
                    <p className="text-gray-700">{aboutData.vision}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">قيمنا</h4>
                  <p className="text-gray-700 font-medium">{aboutData.values}</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-[#D4AF37] mb-2">فريقنا</h4>
                  <p className="text-gray-700">{aboutData.teamDescription}</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
